import { ParseCreateProspectData } from './registrations.types'
import {
  EL_PRODUCT_CODES,
  GAS_PRODUCT_CODES,
  OPT_IN_POINTS,
  SIGN_CHANNELS,
  SIMULATION_TYPES,
  UpsertProspectRequestBody,
  USAGE_GRADES
} from '@boltenergy-be/shared'
import { RootState, store } from 'store'
import { getProductToSelect, getSimulatedPrice } from 'utils/price'
import { getUpperCaseLanguage } from 'utils/format'
import { getFixedPricesForApiRequests, getSharedMarketingDataForRequestBody } from './registrations.utils'
import { removeNonNumbers } from 'utils/helpers'
import { determineSimulationUsage } from 'features/simulation/utils'
import { formatPhoneToE164 } from '@boltenergy-be/design-system/utils'

/**
 * Maps the CreateProspectData & returns it
 *
 * @param {ParseCreateProspectData} data
 * @return {UpsertProspectRequestBody}
 */
export const parseCreateProspectData = ({
  language,
  producer,
  startedFulfillmentFlow,
  customProduct
}: ParseCreateProspectData): UpsertProspectRequestBody => {
  const { app, marketing, customer } = store.getState() as RootState
  const { inputs, fixedPrices } = customer
  const { personalData, deliveryAddress, simulation, meterDetails } = inputs

  // Simulation leads are submitted before registration, so they never carry an enterprise number.
  if (startedFulfillmentFlow && personalData.isCompany && !personalData.companyNumber) {
    throw new Error('Company number is required for company accounts.')
  }

  const price = getSimulatedPrice(customProduct)
  const product = customProduct || getProductToSelect()

  return {
    account: {
      birthDate: personalData.dateOfBirth || undefined,
      company: personalData.isCompany
        ? {
            name: personalData?.companyName,
            creditCheck: personalData?.companyNumber ? marketing.executeCreditCheck : undefined,
            enterpriseNumber: personalData?.companyNumber ? removeNonNumbers(personalData.companyNumber) : undefined,
            legalForm: personalData?.legalForm,
            vatApplication: personalData?.subjectToVat
          }
        : undefined
    },
    contact: {
      contactMoment: personalData.contactMoment,
      email: personalData.email,
      firstName: personalData.firstName,
      lastName: personalData.lastName,
      phoneMobile: formatPhoneToE164(personalData.mobilePhone),
      language: getUpperCaseLanguage(language)
    },
    contract: {
      prices:
        simulation.chosenSimulationType === SIMULATION_TYPES.eav
          ? {}
          : {
              fixed: getFixedPricesForApiRequests(fixedPrices, simulation.chosenSimulationType),
              forcedAmount: simulation.forcedAmount || undefined,
              simulation: price
            },
      producerId: producer.id,
      products: {
        electricity: (product && EL_PRODUCT_CODES[product]) || EL_PRODUCT_CODES.VariableOn,
        gas: product && GAS_PRODUCT_CODES[product]
      },
      promoCodeSfId: simulation?.promoCode?.promoCodeId,
      referrerSfContactId: simulation?.referralCode?.referrer?.id,
      usage:
        simulation.chosenSimulationType === SIMULATION_TYPES.express ||
        simulation.chosenSimulationType === SIMULATION_TYPES.pricingSimulation
          ? simulation.usage
          : determineSimulationUsage(USAGE_GRADES.low)!,
      deliveryAddress: {
        postalCode: Number(deliveryAddress.address.postalCode)
      }
    },
    marketing: {
      ...getSharedMarketingDataForRequestBody(marketing, simulation.chosenSimulationType, marketing.signChannel?.[0] || SIGN_CHANNELS.none),
      optinCta: marketing.optinCta,
      optinPoint: startedFulfillmentFlow ? OPT_IN_POINTS.websiteRegistration : OPT_IN_POINTS.websiteSimulation,
      signChannel: marketing.signChannel?.[0] || SIGN_CHANNELS.none
    },
    meter: {
      counterType: meterDetails.counterType,
      meterType: simulation.meterType
    },
    opportunityId: app.opportunityId || undefined,
    solarPanels: meterDetails.hasSolarPanels,
    walloniaSolarPanelsSince2024: meterDetails.walloniaSolarPanelsSince2024
  } satisfies UpsertProspectRequestBody
}
